import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useAppUpdateBlockingStore } from "@/stores/appUpdateBlockingStore";
import { useEnvironmentStore } from "@/stores/environmentStore";
import { useVersionUpdatePolling } from "@/composables/shared/useVersionUpdatePolling";

/**
 * Reloads the tab when a new app version is deployed, and tells the other tabs
 * to do the same so they need not wait out their own poll interval.
 *
 * Two guards stop that turning into a reload storm, and both are page-load
 * scoped rather than mount scoped - App.vue keys the layout by name, so every
 * layout switch remounts whatever renders this:
 *
 * 1. `detectedLocally` - a tab that only received a broadcast reloads
 *    silently, otherwise tabs echo "new version" at each other forever.
 * 2. `reloadRecord` - a tab that comes back up still running the version it
 *    just reloaded away from is being served a stale bundle; reloading again
 *    cannot fix that, so it halts and shows a banner instead.
 *
 * The reload itself is delayed - see `RELOAD_DELAY_MS`.
 */

const BROADCAST_CHANNEL_NAME = "orbis-app-version-reload";
const RELOADED_FOR_VERSION_KEY = "orbis-app-version-reload-for";

/**
 * How long a halt suppresses further reloads. Far longer than the 60s poll
 * interval so a stale CDN cannot drive a loop, short enough that a tab left
 * open all day still picks up a later deploy on its own.
 */
const STALE_RELOAD_HALT_MS = 10 * 60 * 1000;

/**
 * How long to sit on a detected update before reloading. Mid-rollout the
 * replicas serve different builds, so reloading on the first sighting can 404
 * on a bundle or land back on the old version. Comfortably over a rollout,
 * which readiness probes put at ~10-25s.
 */
const RELOAD_DELAY_MS = 30 * 1000;

interface ReloadRecord {
  version: string;
  at: number;
}

function readReloadRecord(): ReloadRecord | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(RELOADED_FOR_VERSION_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;

    const { version, at } = parsed as Partial<ReloadRecord>;
    if (typeof version !== "string" || typeof at !== "number") return null;

    return { version, at };
  } catch {
    // sessionStorage blocked or unavailable - we lose the guard and fall back
    // to reloading once more.
    return null;
  }
}

function rememberReloadRecord(version: string) {
  try {
    const record: ReloadRecord = { version, at: Date.now() };
    globalThis.sessionStorage?.setItem(RELOADED_FOR_VERSION_KEY, JSON.stringify(record));
  } catch {
    // ignore - see readReloadRecord
  }
}

// Read once per page load, so a remount cannot re-open the guard.
const reloadRecord = readReloadRecord();

let detectedLocally = true;

// Module scoped too - a locally held timer would restart on remount and never
// elapse.
let reloadTimeoutId: ReturnType<typeof setTimeout> | undefined;

// Shared by however many callers mount, so overlapping layouts cannot
// double-broadcast.
let broadcastChannel: BroadcastChannel | undefined;
let channelSubscribers = 0;

function acquireBroadcastChannel(onNewVersion: () => void) {
  channelSubscribers += 1;
  if (broadcastChannel) return;
  if (globalThis.BroadcastChannel === undefined) return;

  broadcastChannel = new globalThis.BroadcastChannel(BROADCAST_CHANNEL_NAME);
  broadcastChannel.addEventListener("message", () => {
    detectedLocally = false;
    onNewVersion();
  });
}

function releaseBroadcastChannel() {
  channelSubscribers = Math.max(0, channelSubscribers - 1);
  if (channelSubscribers > 0) return;

  broadcastChannel?.close();
  broadcastChannel = undefined;
}

export function useVersionReload() {
  const { hasDirtyFormBlockingRefresh } = storeToRefs(useAppUpdateBlockingStore());
  const { pendingServerUpdate, markPendingServerUpdate } = useVersionUpdatePolling();
  const { version } = useEnvironmentStore();

  // A halted tab keeps being told "newer version available", so
  // `pendingServerUpdate` never changes and never re-fires the watcher. This
  // ticks when the halt lapses, which is what gets the tab moving again.
  const now = ref(Date.now());

  const isHaltActive = () =>
    reloadRecord !== null &&
    reloadRecord.version === version &&
    now.value - reloadRecord.at < STALE_RELOAD_HALT_MS;

  const isStaleAfterReload = computed(() => pendingServerUpdate.value && isHaltActive());

  /** True while an update is known but deliberately not being applied. */
  const isUpdateDeferred = computed(
    () =>
      pendingServerUpdate.value && (hasDirtyFormBlockingRefresh.value || isStaleAfterReload.value)
  );

  function tryApplyPendingUpdate() {
    if (!pendingServerUpdate.value) return;
    if (hasDirtyFormBlockingRefresh.value) return;
    if (isStaleAfterReload.value) return;
    if (reloadTimeoutId !== undefined) return;

    if (detectedLocally) {
      broadcastChannel?.postMessage({ type: "new-version" });
    }

    reloadTimeoutId = setTimeout(() => {
      reloadTimeoutId = undefined;

      // A form can be touched during the wait; reloading would lose that work.
      if (hasDirtyFormBlockingRefresh.value) return;

      // Recorded no matter who detected the update: if we come back up still
      // running this version, the reload did not take and must not be repeated.
      rememberReloadRecord(version);

      globalThis.location.reload();
    }, RELOAD_DELAY_MS);
  }

  watch([pendingServerUpdate, hasDirtyFormBlockingRefresh, now], tryApplyPendingUpdate);

  let haltExpiryTimeoutId: ReturnType<typeof setTimeout> | undefined;

  onMounted(() => {
    acquireBroadcastChannel(markPendingServerUpdate);

    if (!isHaltActive()) return;

    const remainingMs = reloadRecord!.at + STALE_RELOAD_HALT_MS - now.value;
    haltExpiryTimeoutId = setTimeout(() => {
      now.value = Date.now();
    }, remainingMs);
  });

  onUnmounted(() => {
    releaseBroadcastChannel();
    if (haltExpiryTimeoutId !== undefined) clearTimeout(haltExpiryTimeoutId);
  });

  return { isUpdateDeferred };
}
